import {Box, Typography, Icon} from '@mui/material';
import {OUTLINE, SUBTITLE1} from '@/utils/constants';
import {CodeForm} from './CodeForm';
import {FC, useEffect, useState} from 'react';
import {ReactComponent as SecurityIcon} from '@icons/security-safe.svg';
import Button from '@/shared/components/Button';
import {twoStepAuthenticationStyles} from './TwoStepAuthentication.styles';
import {ITwoFactorSetupResponse} from '@/utils/types/superAdmin.interface';
import {getErrorMessage} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {adminStorage, SuperAdminService} from '@/utils/services';
import {STORAGE_KEY} from '@/utils/enums';

type TwoStepAuthenticationModalStep1Props = {
  setupData: ITwoFactorSetupResponse | null;
  onClose: () => void;
  onConfirmed: () => void;
  onExpired: () => void;
};

const QrCodeStep: FC<TwoStepAuthenticationModalStep1Props> = ({setupData, onClose, onConfirmed, onExpired}) => {
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);

  const codeValue = code.join('');
  const isCodeComplete = /^\d{6}$/.test(codeValue);
  const formattedKey = setupData?.manualEntryKey.match(/.{1,4}/g)?.join(' ') ?? setupData?.manualEntryKey;

  useEffect(() => {
    const ms = new Date(setupData?.expiresAt ?? '').getTime() - Date.now();
    const timer = setTimeout(onExpired, Math.max(ms, 0));
    return () => clearTimeout(timer);
  }, [setupData?.expiresAt]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const {data} = await SuperAdminService.confirmAdmin2FactorSetup({code: codeValue});
      adminStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, data.jwtBearerToken);
      adminStorage.set(STORAGE_KEY.REFRESH_TOKEN, data.refreshToken);
      onConfirmed();
    } catch (error) {
      toast.error(getErrorMessage(error));
      setCode(Array(6).fill(''));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Icon sx={twoStepAuthenticationStyles.icon}>
        <SecurityIcon />
      </Icon>
      <Typography sx={twoStepAuthenticationStyles.mainTitle}>Two-Step Authentication</Typography>
      <Box sx={twoStepAuthenticationStyles.wrapper}>
        <Box sx={twoStepAuthenticationStyles.content}>
          <Typography variant={SUBTITLE1} sx={twoStepAuthenticationStyles.title}>
            Step 1. Download the app for authentication
          </Typography>
          <Typography sx={twoStepAuthenticationStyles.description}>
            Download the
            <a
              href="https://www.google.com/search?q=google+authenticator+app+download&sca_esv=acc27629dfc230bb&sxsrf=AHTn8zpHLiUcf2sC51KZwxVKNZRW2Nft8A%3A1746795527923&ei=B_wdaLSQONTOwPAP-O7e8QU&oq=google+authenticator+app+&gs_lp=Egxnd3Mtd2l6LXNlcnAiGWdvb2dsZSBhdXRoZW50aWNhdG9yIGFwcCAqAggBMgoQIxiABBgnGIoFMggQABiABBjLATIIEAAYgAQYywEyCBAAGIAEGMsBMggQABiABBjLATIIEAAYgAQYywEyCBAAGIAEGMsBMggQABiABBjLATIIEAAYgAQYywEyCBAAGIAEGMsBSOogUMQDWN4LcAF4AZABAJgBWKABnQOqAQE1uAEDyAEA-AEBmAIGoAK5A8ICChAAGLADGNYEGEfCAg0QABiABBiwAxhDGIoFwgIGEAAYFhgewgIIEAAYgAQYogSYAwCIBgGQBgqSBwE2oAfxK7IHATW4B7MD&sclient=gws-wiz-serp"
              target="_blank"
              rel="noreferrer noopener"
            >
              Google Authenticator
            </a>
            app if you do not have it for next steps.
          </Typography>
        </Box>
        <Box sx={twoStepAuthenticationStyles.content}>
          <Typography variant={SUBTITLE1} sx={twoStepAuthenticationStyles.title}>
            Step 2. Scan this QR code or copy key
          </Typography>
          <Typography>
            Scan QR code through the authentication app or enter the key into the authentication app.
          </Typography>

          <Box sx={twoStepAuthenticationStyles.qr}>
            <img src={setupData?.qrCodeDataUrl} alt="QR Code" />

            <Typography variant={SUBTITLE1}>{formattedKey}</Typography>
          </Box>
        </Box>
        <Box sx={twoStepAuthenticationStyles.content}>
          <Typography variant={SUBTITLE1} sx={twoStepAuthenticationStyles.title}>
            Step 3. Enter the 6-digit code
          </Typography>
          <Typography>
            After scanning QR code or entering a key, the authentication app will generate a 6-digit code.
          </Typography>
          <CodeForm code={code} handleCode={setCode} />
        </Box>
      </Box>
      <Box sx={twoStepAuthenticationStyles.buttonWrapper}>
        <Button title="Cancel" variant={OUTLINE} onClick={onClose} />
        <Button title="Confirm" onClick={handleConfirm} disabled={!isCodeComplete || isLoading} />
      </Box>
    </Box>
  );
};

export default QrCodeStep;
